import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchPrices } from '@/features/billing/billingThunks';
import { selectPrices, selectSubscription } from '@/features/billing/billingSlice';
import {
  formatPrice,
  getPlanDisplayName,
  getPriceByInterval,
} from '@/components/billing/usageExpiredUtils';
import { isPartnerOfferPlan } from '@/lib/partnerOfferPlan';
import { getRenewalSummary } from '@/lib/subscriptionPricing';
import { getLocale } from '@/lib/utils';
import Title from '@/components/ui/Title';
import ChangePlanDialog from '@/components/billing/ChangePlanDialog';
import UsageExpiredAlert from '@/components/billing/UsageExpiredAlert';
import { Badge } from '@/components/ui/badge';
import FlowCancelSubscribs from './FlowCancelSubscribs';
import { useTranslation } from 'react-i18next';

export default function CurrentSubscribs() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');
  const subscription = useAppSelector(selectSubscription);
  const prices = useAppSelector(selectPrices);
  const workspaceId = useAppSelector((s) => s.workspace.currentWorkspace?.id);
  const promoCode = useAppSelector((s) => s.workspace.currentWorkspace?.promoCode);
  const subscriptionStatus = useAppSelector(
    (s) => s.workspace.currentWorkspace?.subscriptionStatus,
  );
  const legacyTrialEndDate = useAppSelector(
    (s) => s.workspace.currentWorkspace?.legacyTrialEndDate,
  );
  const isFreePlan = subscriptionStatus === 'NONE';
  const usage = useAppSelector((s) => s.billing.usage.data);
  const isCustomPlan = usage?.planName === 'Custom';
  const isPartnerOffer = isPartnerOfferPlan(subscriptionStatus, usage?.planName);
  // Le back ne l'accorde qu'à l'Owner ; champ absent = on masque plutôt que d'exposer la résiliation.
  const canCancelSubscription = subscription?.canCancelSubscription === true;
  const isSubscriptionTrialing = subscription?.status === 'trialing';

  const hasBillingRegularizationBanner = Boolean(
    useAppSelector((s) => s.workspace.currentWorkspace?.billingRegularizationBanner),
  );

  useEffect(() => {
    if (workspaceId) dispatch(fetchPrices({ workspaceId, ...(promoCode && { promoCode }) }));
  }, [workspaceId, promoCode, dispatch]);

  const planName = isPartnerOffer
    ? getPlanDisplayName(usage?.planName)
    : isFreePlan
      ? 'Free'
      : isCustomPlan
        ? 'VIP'
        : getPlanDisplayName(subscription?.planName) || t('subscription.defaultPlan');
  const formattedDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  const planChangeFormatted =
    subscription?.planChangeDate != null && subscription.planChangeDate !== ''
      ? new Date(subscription.planChangeDate).toLocaleDateString(getLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : null;

  const hasScheduledPlanChange = Boolean(planChangeFormatted);

  const renewal = subscription
    ? getRenewalSummary(
        subscription,
        getPriceByInterval(
          prices,
          subscription.planName || t('subscription.defaultPlan'),
          subscription.interval as 'month' | 'year',
        ),
      )
    : null;

  const cancelEndFormatted =
    subscription?.cancelAt != null && subscription.cancelAt !== ''
      ? new Date(subscription.cancelAt).toLocaleDateString(getLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : formattedDate;

  const partnerOfferEndFormatted =
    isPartnerOffer && legacyTrialEndDate
      ? new Date(legacyTrialEndDate).toLocaleDateString(getLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : null;

  const isPauseScheduled = subscription?.pause?.status === 'SCHEDULED';
  const pauseStartFormatted = subscription?.pause?.startsAt
    ? new Date(subscription.pause.startsAt).toLocaleDateString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;
  const pauseEndFormatted = subscription?.pause?.endsAt
    ? new Date(subscription.pause.endsAt).toLocaleDateString(getLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-2 rounded-lg border-[#2C6CFF]">
      <div className="flex flex-col gap-2 min-w-0">
        <Title level={2}>
          {t('subscription.titlePrefix')} <span className="text-[#2C6CFF]">{planName}</span>
        </Title>

        {partnerOfferEndFormatted && (
          <p className="text-secondary">
            {t('subscription.partnerOfferUntil')}{' '}
            <span className="text-black font-medium">{partnerOfferEndFormatted}</span>
          </p>
        )}

        {!isFreePlan && subscription && (
          <>
            {/* Un plan VIP est négocié hors catalogue Stripe : sans montant, `renewal` est null et la
                ligne disparaît, que l'usage soit déjà chargé ou non. Les dates, elles, restent affichées. */}
            {!isCustomPlan && renewal && (
              <>
                <p className="text-secondary">
                  {renewal.hasDiscount && (
                    <span className="line-through text-tertiary mr-2">
                      {formatPrice(renewal.fullAmountCents, subscription.currency)}
                    </span>
                  )}
                  {formatPrice(renewal.nextAmountCents, subscription.currency)}
                  {subscription.interval === 'year'
                    ? t('subscription.intervalYear')
                    : t('subscription.intervalMonth')}
                </p>
                {promoCode && renewal.hasDiscount && (
                  <p className="text-sm text-secondary">
                    {t('subscription.promoCodeLine', {
                      code: promoCode,
                      original: formatPrice(renewal.fullAmountCents, subscription.currency),
                      final: formatPrice(renewal.nextAmountCents, subscription.currency),
                      discount: formatPrice(renewal.discountCents, subscription.currency),
                    })}
                  </p>
                )}
              </>
            )}
            {subscription.cancelAtPeriodEnd && cancelEndFormatted ? (
              <p className="text-secondary">
                {t('subscription.cancelEnd')}{' '}
                <span className="text-black font-medium">{cancelEndFormatted}</span>
              </p>
            ) : isPauseScheduled ? (
              <>
                {pauseStartFormatted && (
                  <p className="text-secondary">
                    {t('subscription.pauseScheduledFrom', {
                      count: subscription?.pause?.months ?? 0,
                    })}{' '}
                    <span className="text-black font-medium">{pauseStartFormatted}</span>
                  </p>
                )}
                {pauseEndFormatted && (
                  <p className="text-secondary">
                    {t('subscription.nextPaymentAfterPause')}{' '}
                    <span className="text-black font-medium">{pauseEndFormatted}</span>
                  </p>
                )}
                <p className="text-black font-semibold">{t('subscription.pauseNotBilled')}</p>
              </>
            ) : (
              <>
                {formattedDate && (
                  <p className="text-secondary">
                    {t('subscription.nextPayment')}{' '}
                    <span className="text-black font-medium">{formattedDate}</span>
                  </p>
                )}
                {hasScheduledPlanChange && subscription && !isFreePlan && (
                  <Badge className="bg-medium text-black text-base w-full sm:w-fit whitespace-normal shrink text-left">
                    {t('subscription.scheduledPlanChange', {
                      planName: getPlanDisplayName(subscription.planName),
                      date: planChangeFormatted,
                    })}
                  </Badge>
                )}
              </>
            )}
          </>
        )}
      </div>
      {(!isFreePlan || isSubscriptionTrialing) &&
        !isCustomPlan &&
        !hasBillingRegularizationBanner && (
          <div className="flex flex-col items-stretch sm:items-end justify-between gap-4 sm:self-stretch">
            <ChangePlanDialog />
            {canCancelSubscription && <FlowCancelSubscribs />}
          </div>
        )}
      {isFreePlan &&
        !isSubscriptionTrialing &&
        !isCustomPlan &&
        !hasBillingRegularizationBanner && <UsageExpiredAlert isConfig />}
    </div>
  );
}
